import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye } from "lucide-react";
import api, { money, formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import AdminLayout from "@/components/admin/AdminLayout";
import { TableShell, Th, Td, EmptyRow } from "@/components/admin/DataTable";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function Customers() {
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get(`/customers?search=${search}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search]);

  const view = async (id) => { const r = await api.get(`/customers/${id}`); setActive(r.data); };
  const save = async () => {
    try { await api.put(`/customers/${edit.id}`, { name: edit.name, mobile: edit.mobile, cnic: edit.cnic, city: edit.city, address: edit.address }); toast.success("Saved"); setEdit(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/customers/${id}`); toast.success("Deactivated"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const removePermanent = async (id) => { try { await api.delete(`/customers/${id}?permanent=true`); toast.success("Customer permanently deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const toggleStatus = async (c) => {
    try { await api.put(`/customers/${c.id}`, { is_active: c.is_active === false }); toast.success(c.is_active === false ? "Customer activated" : "Customer set inactive"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout title="Customers">
      <TableShell search={search} onSearch={setSearch} searchPlaceholder="Search name, email, mobile, ID...">
        <thead className="border-b border-border bg-secondary/40"><tr><Th>Customer ID</Th><Th>Name</Th><Th>Email</Th><Th>Mobile</Th><Th>Orders</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr></thead>
        <tbody className="divide-y divide-border">
          {items.length === 0 ? <EmptyRow colSpan={7} /> : items.map((c) => (
            <tr key={c.id} className="hover:bg-secondary/30" data-testid={`customer-row-${c.id}`}>
              <Td className="font-mono text-xs">{c.customer_no || "—"}</Td>
              <Td className="font-medium">{c.name}</Td><Td>{c.email}</Td><Td>{c.mobile || "—"}</Td><Td>{c.order_count}</Td>
              <Td>{c.is_active === false ? <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive">Inactive</span> : <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">Active</span>}</Td>
              <Td className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => view(c.id)} data-testid={`view-customer-${c.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                  {can("customers", "edit") && <Button size="sm" variant="outline" onClick={() => toggleStatus(c)} data-testid={`status-customer-${c.id}`} className={c.is_active === false ? "text-emerald-600" : "text-amber-600"}>{c.is_active === false ? "Activate" : "Set Inactive"}</Button>}
                  {can("customers", "edit") && <Button size="sm" variant="outline" onClick={() => setEdit(c)} data-testid={`edit-customer-${c.id}`}>Edit</Button>}
                  {can("customers", "delete") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="text-destructive" data-testid={`del-customer-${c.id}`}>Delete</Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Deactivate customer?</AlertDialogTitle><AlertDialogDescription>Logged in audit trail.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(c.id)}>Confirm</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                  {can("customers", "delete") && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="destructive" data-testid={`perm-del-customer-${c.id}`}>Permanent Delete</Button></AlertDialogTrigger>
                      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle className="text-destructive">Permanently delete this customer?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the customer AND their orders, requests, payments and complaints. This cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removePermanent(c.id)} data-testid={`confirm-perm-del-${c.id}`}>Permanently Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          {active && (<>
            <DialogHeader><DialogTitle>{active.name} · {active.customer_no}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-border p-4 text-sm">
              <div><p className="text-muted-foreground">Email</p><p>{active.email}</p></div>
              <div><p className="text-muted-foreground">Mobile</p><p>{active.mobile || "—"}</p></div>
              <div><p className="text-muted-foreground">CNIC</p><p>{active.cnic || "—"}</p></div>
              <div><p className="text-muted-foreground">City</p><p>{active.city || "—"}</p></div>
              <div className="col-span-2"><p className="text-muted-foreground">Address</p><p>{active.address || "—"}</p></div>
            </div>
            <div><h4 className="mb-2 font-semibold">Orders ({active.orders?.length || 0})</h4>
              <div className="grid gap-2">{(active.orders || []).map((o) => <div key={o.id} className="flex items-center justify-between rounded-lg bg-secondary/50 px-3 py-2 text-sm"><span>{o.order_no} · {o.product_name}</span><StatusBadge status={o.status} /></div>)}</div>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent>
          {edit && (<>
            <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="cust-edit-name" /></div>
              <div><Label>Mobile</Label><Input value={edit.mobile || ""} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} /></div>
              <div><Label>CNIC</Label><Input value={edit.cnic || ""} onChange={(e) => setEdit({ ...edit, cnic: e.target.value })} /></div>
              <div><Label>City</Label><Input value={edit.city || ""} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={edit.address || ""} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></div>
              <Button onClick={save} className="col-span-2 rounded-full" data-testid="cust-save">Save</Button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
